import { Box, FormLabel, Stack, Typography } from '@mui/material'

import { C1Button } from 'c1ui'
import { APIError } from 'components/core/errors/api'
import { SelectOneUserHook } from 'components/core/search/users'
import { TableGrid } from 'components/core/table-grid'
import { Info } from 'components/core/typeface'
import { UserDetailsStyles } from 'components/pages/user/details/styles'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { UserValue } from 'lib/actions/user'
import { UpdateUserRequest } from 'pbts/c1/api/user/v1/user'
import { FieldMask } from 'pbts/google/protobuf/field_mask'
import { FC, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { updateUserApi } from 'redux_toolkit/users'

export const DelegatedUserDetails: FC<{ userId: string; delegatedUser?: UserValue }> = ({
    userId,
    delegatedUser: initialDelegatedUser,
}) => {
    const [isEditable, setEditable] = useState<boolean>(false)
    const [delegatedUser, setDelegatedUser] = useState<UserValue | null>(initialDelegatedUser || null)

    const intl = useIntl()

    const onSelectedUser = useCallback((value: UserValue | null): void => setDelegatedUser(value), [setDelegatedUser])
    const { mutate: updateUser, error, inProgress } = updateUserApi()

    const newDelegateId = delegatedUser?.id || ''
    const updateUserDelegate = useCallback(
        (): Promise<void> =>
            updateUser(
                UpdateUserRequest.create({
                    user: { id: userId, delegatedUserId: newDelegateId },
                    updateMask: FieldMask.create({ paths: ['delegated_user_id'] }),
                    expandMask: { paths: ['*'] },
                }),
            ).then(() => setEditable(false)).catch((e) => e),
        [userId, newDelegateId, setEditable, updateUser],
    )

    const delegatedButton = isEditable ? (
        <Box sx={UserDetailsStyles.buttonsContainer}>
            <Box sx={UserDetailsStyles.cancelButton}>
                <C1Button
                    variant="outlined"
                    onClick={(): void => {
                        setDelegatedUser(initialDelegatedUser || null)
                        setEditable(false)
                    }}
                    disabled={inProgress}
                >
                    <FormattedMessage id="components_delegated_user_cancel" defaultMessage="Cancel" />
                </C1Button>
            </Box>
            <Box>
                <C1Button variant="contained" onClick={updateUserDelegate} disabled={inProgress}>
                    <FormattedMessage id="components_delegated_user_save" defaultMessage="Save" />
                </C1Button>
            </Box>
        </Box>
    ) : (
        <C1Button variant="text" onClick={(): void => setEditable(true)}>
            <FormattedMessage id="components_delegated_user_edit" defaultMessage="Edit" />
        </C1Button>
    )

    return (
        <TableGrid
            title={<FormattedMessage id="user_delegation_title" defaultMessage="Delegation" />}
            buttons={delegatedButton}
        >
            <Stack spacing={3}>
                <Box>
                    <APIError
                        err={error}
                        title={
                            <FormattedMessage
                                id="components_user_details_set_delegated_user_error"
                                defaultMessage="Error setting delegated user"
                            />
                        }
                    />
                </Box>
                <Info>
                    <FormattedMessage
                        id="component_user_delegate_desc"
                        defaultMessage="Tasks assigned to this user will be automatically reassigned to their delegate unless the task's policy doesn't allow delegation."
                    />
                </Info>
                <Box>
                    <FormLabel sx={UserDetailsStyles.delegateLabel}>
                        <FormattedMessage id="user_details_delegate_label" defaultMessage="Delegate" />
                    </FormLabel>
                    <Box>
                        {!isEditable &&
                            (delegatedUser ? (
                                <UserDisplayName user={delegatedUser} includeDetails={false} showTooltip />
                            ) : (
                                <Typography sx={UserDetailsStyles.empty} noWrap>
                                    <FormattedMessage id="delegated_user_empty_state" defaultMessage="None" />
                                </Typography>
                            ))}
                        {isEditable && (
                            <SelectOneUserHook
                                value={delegatedUser}
                                onSelected={onSelectedUser}
                                placeholder={intl.formatMessage({
                                    id: 'component_select_delegated_user_placeholder',
                                    defaultMessage: 'Search for a user',
                                })}
                                baseRequest={{
                                    excludeIds: [userId],
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Stack>
        </TableGrid>
    )
}
